// src/pages/TeamCalendarPage.tsx

import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../contexts/AuthAdapter';
import WeeklyCalendarView from '../components/WeeklyCalendarView';
import ManagerWeeklyCalendarView from '../components/ManagerWeeklyCalendarView';
import CreateProjectModal from '../components/CreateProjectModal';
import CreateTaskModal from '../components/CreateTaskModal';
import { SidebarPortal } from '../../shared/components/SidebarPortal';
import { Button, Tabs, EmptyState } from '../../shared/components/ui';
import { Project, Task } from '../services/api/types';
import { apiService } from '../services/api';

const TeamCalendarPage: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  // Criação de tarefa a partir do clique numa célula (funcionário x dia)
  const [taskModalData, setTaskModalData] = useState<{
    employeeId: number;
    employeeName: string;
    date: Date;
    projectId: number;
  } | null>(null);

  // View mode state for Team Overview / Project Detail toggle
  const [viewMode, setViewMode] = useState<'overview' | 'project'>('overview');

  // Check if user can create projects (managers, directors, admins)
  const canCreateProject = user?.role && (
    ['manager', 'director', 'admin', 'project manager'].includes(user.role.toLowerCase()) ||
    user.role.toLowerCase().includes('manager') ||
    user.role.toLowerCase().includes('director') ||
    user.role.toLowerCase().includes('admin') ||
    user.role === 'Project Manager'
  );

  // Get current week start (Monday)
  const getWeekStart = () => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  const [currentWeek, setCurrentWeek] = useState(getWeekStart());

  // Navigate between weeks
  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeek = new Date(currentWeek);
    newWeek.setDate(newWeek.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeek(newWeek);
  };

  // Abre o modal de tarefa para o funcionário/dia clicado no calendário da equipe
  const handleCreateTask = (employeeId: number, employeeName: string, date: Date) => {
    const employeeProject = projects.find(
      (p) => Array.isArray(p.assigned_employee_ids) && p.assigned_employee_ids.includes(employeeId)
    );
    if (!employeeProject) return;
    setTaskModalData({ employeeId, employeeName, date, projectId: employeeProject.id });
  };

  // Handle project creation
  const handleProjectCreated = async () => {
    setIsProjectModalOpen(false);
    // Refresh projects list
    try {
      const projectsResponse = await apiService.projects.getProjects();
      setProjects(projectsResponse);
    } catch (error) {
      // Silent error handling
    }
  };

  // Fetch projects and tasks
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Fetch projects
        const projectsResponse = await apiService.projects.getProjects();
        setProjects(projectsResponse);

        // For employees, select first project; for managers, they can see all
        if (projectsResponse.length > 0 && user.role?.toLowerCase() === 'employee') {
          const firstProject = projectsResponse[0];
          setSelectedProject(firstProject);

          // Fetch tasks for the selected project - use employee_id for proper filtering
          const employeeIdForTasks = user.employee_id || user.id;
          const tasksResponse = await apiService.tasks.getTasksByUserAndProject(
            employeeIdForTasks,
            firstProject.id
          );
          setTasks(tasksResponse);
        }
        
      } catch (error) {
        console.error('Error fetching team calendar data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const isEmployee = user?.role?.toLowerCase() === 'employee';

  const sidebarContent = (
    <>
      {/* Create New Project Button - Only for managers/admins */}
      {canCreateProject && (
        <Button
          variant="primary"
          fullWidth
          className="mb-4"
          onClick={() => setIsProjectModalOpen(true)}
        >
          Criar novo projeto
        </Button>
      )}

      {/* Project Selection */}
      <div className="mb-6">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-foreground-muted">
          Seus projetos ({projects.length})
        </h3>

        {projects.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            {projects.map((project: Project) => {
              const isActive = selectedProject?.id === project.id;
              return (
                <button
                  key={project.id}
                  onClick={() => {
                    setSelectedProject(project);
                    // Switch to project detail mode when a project is selected (for managers)
                    if (!isEmployee) {
                      setViewMode('project');
                    }
                  }}
                  className={`flex w-full items-center gap-2 rounded-lg border-l-2 px-3 py-2.5 text-left text-sm transition-colors ${
                    isActive
                      ? 'border-l-brand bg-brand/10 font-semibold text-brand'
                      : 'border-l-transparent font-medium text-foreground-muted hover:bg-surface-3 hover:text-foreground'
                  }`}
                >
                  <span className="truncate">{project.name}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="rounded-lg bg-surface-3 px-3 py-2.5 text-center">
            <p className="text-sm text-foreground-muted">Nenhum projeto atribuído</p>
          </div>
        )}
      </div>
    </>
  );

  if (!user) {
    return (
      <EmptyState
        title="Acesso restrito"
        description="Faça login para acessar o calendário da equipe."
      />
    );
  }

  if (loading) {
    return (
      <>
        <SidebarPortal>{sidebarContent}</SidebarPortal>
        <EmptyState title="Carregando calendário da equipe..." />
      </>
    );
  }

  return (
    <>
      <SidebarPortal>{sidebarContent}</SidebarPortal>

        <div className="border-b border-border bg-surface-1 px-8 py-6">
          <div>
            <h1 className="font-display text-2xl font-semibold text-foreground">
              Calendário da Equipe
            </h1>
            <p className="mt-1 text-[15px] text-foreground-muted">
              {isEmployee
                ? 'Visualize e gerencie suas tarefas e agenda da semana'
                : 'Gerencie agendas da equipe e atribua tarefas entre os projetos'
              }
            </p>
          </div>
        </div>

        <div className="px-8 pb-8 pt-6">
          {isEmployee ? (
            selectedProject ? (
              <WeeklyCalendarView
                project={selectedProject}
                weekStart={currentWeek}
              />
            ) : (
              <EmptyState
                title="Nenhum projeto atribuído"
                description="Entre em contato com seu gestor para ser atribuído a um projeto."
              />
            )
          ) : (
            <>
              {/* View Mode Toggle for Managers/Supervisors */}
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <Tabs
                  value={viewMode}
                  onChange={(v) => setViewMode(v as 'overview' | 'project')}
                  tabs={[
                    { value: 'overview', label: 'Visão da Equipe' },
                    { value: 'project', label: 'Detalhe do Projeto' },
                  ]}
                />

                {/* Week Navigation */}
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateWeek('prev')}
                    data-testid="calendar-prev-week"
                    leftIcon={
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    }
                  >
                    Semana anterior
                  </Button>
                  <span className="min-w-[180px] text-center text-sm font-medium text-foreground tabular-nums">
                    {currentWeek.toLocaleDateString('pt-BR', {
                      month: 'short',
                      day: 'numeric'
                    })} - {new Date(currentWeek.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigateWeek('next')}
                    data-testid="calendar-next-week"
                    rightIcon={
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    }
                  >
                    Próxima semana
                  </Button>
                </div>
              </div>

              {/* Render appropriate view based on mode */}
              {viewMode === 'overview' ? (
                <ManagerWeeklyCalendarView
                  projects={projects}
                  weekStart={currentWeek}
                  onCreateTask={handleCreateTask}
                />
              ) : (
                selectedProject ? (
                  <div>
                    <div className="mb-5 rounded-xl border border-border bg-surface-1 p-5 shadow-xs">
                      <h3 className="font-display text-lg font-semibold text-foreground">
                        Projeto: {selectedProject.name}
                      </h3>
                      {selectedProject.description && (
                        <p className="mt-1 text-[15px] text-foreground-muted">
                          {selectedProject.description}
                        </p>
                      )}
                    </div>
                    <ManagerWeeklyCalendarView
                      projects={[selectedProject]}
                      weekStart={currentWeek}
                      onCreateTask={handleCreateTask}
                    />
                  </div>
                ) : (
                  <EmptyState
                    title="Detalhe do projeto"
                    description="Selecione um projeto na barra lateral para ver informações detalhadas e tarefas."
                  />
                )
              )}
            </>
          )}
        </div>

      {/* Create Project Modal */}
      {isProjectModalOpen && (
        <CreateProjectModal
          onClose={() => setIsProjectModalOpen(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {/* Create Task Modal (célula clicada no calendário da equipe) */}
      {taskModalData && (
        <CreateTaskModal
          projectId={taskModalData.projectId}
          selectedDate={taskModalData.date}
          assignedEmployeeIds={[taskModalData.employeeId]}
          onClose={() => setTaskModalData(null)}
          onTaskCreated={() => setTaskModalData(null)}
          availableEmployees={[{ id: taskModalData.employeeId, name: taskModalData.employeeName }]}
        />
      )}
    </>
  );
};

export default TeamCalendarPage;